"use client";

import { useState } from "react";
import { SelectField, TextField } from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, HINT } from "@/components/tool-kit/styles";
import {
  coordinateText,
  type PlaceFieldValues,
} from "@/lib/tool-kit/astronomy/place-input";
import { findPlace, PLACES, ZONE_IDS } from "@/lib/tool-kit/astronomy/places";
import { isKnownZone } from "@/lib/tool-kit/astronomy/zone";

export interface PlaceFieldLabels {
  place: string;
  custom: string;
  latitude: string;
  longitude: string;
  latitudeHint: string;
  zone: string;
  locate: string;
  locating: string;
  locateFailed: string;
  placeNames: Record<string, string>;
}

/**
 * The place and time-zone boxes shared by the sun tools: a city menu, latitude and
 * longitude, a zone menu and a "use my location" button. The browser asks permission for
 * the location itself; the coordinates are used on the page and sent nowhere.
 */
export function PlaceFields({
  prefix,
  labels,
  values,
  onChange,
}: {
  prefix: string;
  labels: PlaceFieldLabels;
  values: PlaceFieldValues;
  onChange: (patch: Partial<PlaceFieldValues>) => void;
}) {
  const [status, setStatus] = useState<"idle" | "busy" | "failed">("idle");
  const zones = ZONE_IDS.includes(values.zone)
    ? ZONE_IDS
    : [...ZONE_IDS, values.zone];

  const pick = (id: string) => {
    const place = findPlace(id);
    if (place === null) {
      onChange({ place: "custom" });
      return;
    }
    onChange({
      place: id,
      latitude: coordinateText(place.latitude),
      longitude: coordinateText(place.longitude),
      zone: place.zone,
    });
  };

  const locate = () => {
    if (!("geolocation" in navigator)) {
      setStatus("failed");
      return;
    }
    setStatus("busy");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const zone = new Intl.DateTimeFormat().resolvedOptions().timeZone;
        onChange({
          place: "custom",
          latitude: coordinateText(position.coords.latitude),
          longitude: coordinateText(position.coords.longitude),
          ...(isKnownZone(zone) ? { zone } : {}),
        });
        setStatus("idle");
      },
      () => setStatus("failed"),
      { timeout: 10_000 }
    );
  };

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id={`${prefix}-place`}
          label={labels.place}
          onChange={pick}
          options={[
            { value: "custom", label: labels.custom },
            ...PLACES.map((place) => ({
              value: place.id,
              label: labels.placeNames[place.id] ?? place.id,
            })),
          ]}
          value={values.place}
        />
        <SelectField
          id={`${prefix}-zone`}
          label={labels.zone}
          onChange={(zone) => onChange({ zone })}
          options={zones.map((zone) => ({ value: zone, label: zone }))}
          value={values.zone}
        />
        <TextField
          hint={labels.latitudeHint}
          id={`${prefix}-lat`}
          label={labels.latitude}
          numeric
          onChange={(latitude) => onChange({ place: "custom", latitude })}
          value={values.latitude}
        />
        <TextField
          id={`${prefix}-lon`}
          label={labels.longitude}
          numeric
          onChange={(longitude) => onChange({ place: "custom", longitude })}
          value={values.longitude}
        />
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <button
          className={BUTTON_SECONDARY}
          disabled={status === "busy"}
          onClick={locate}
          type="button"
        >
          {status === "busy" ? labels.locating : labels.locate}
        </button>
        {status === "failed" ? (
          <p className={HINT} role="alert">
            {labels.locateFailed}
          </p>
        ) : null}
      </div>
    </div>
  );
}
